import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, Trash } from "@phosphor-icons/react";

/**
 * Uploads an image via POST /api/upload and returns the public URL through onChange.
 * Also supports pasting an external URL directly.
 */
export function ImageUpload({ value, onChange, testid = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [manualUrl, setManualUrl] = useState(value || "");

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    if (!/^image\/(jpe?g|png|gif|webp)$/i.test(file.type)) {
      toast.error("Only JPG, PNG, GIF or WEBP allowed");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", form);
      onChange(data.url);
      setManualUrl(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  return (
    <div className="space-y-2">
      {value ? (
        <div className="relative group">
          <img
            src={value}
            alt="Preview"
            className="w-full max-h-56 object-cover rounded-lg border border-[#E5E0D8]"
          />
          <button
            type="button"
            onClick={() => { onChange(""); setManualUrl(""); }}
            className="absolute top-2 right-2 bg-white/95 border border-[#E5E0D8] rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`${testid}-remove`}
            aria-label="Remove image"
          >
            <Trash size={14} className="text-[#D17A58]" />
          </button>
        </div>
      ) : (
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-[#E5E0D8] rounded-lg p-6 text-center bg-[#F9F8F6] hover:bg-[#EADDC8]/30 transition-colors cursor-pointer"
          onClick={() => inputRef.current?.click()}
          data-testid={`${testid}-dropzone`}
        >
          <UploadSimple size={22} className="mx-auto text-[#5B7B53] mb-2" />
          <p className="text-sm text-[#2C2B29]">
            {uploading ? "Uploading..." : "Drop an image or click to browse"}
          </p>
          <p className="text-xs text-[#5C5A56] mt-1">JPG, PNG, WEBP · under 5 MB</p>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        hidden
        data-testid={`${testid}-input`}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#5C5A56] shrink-0">or paste URL:</span>
        <input
          type="url"
          value={manualUrl}
          onChange={(e) => setManualUrl(e.target.value)}
          onBlur={() => manualUrl !== value && onChange(manualUrl)}
          placeholder="https://..."
          className="w-full px-2.5 py-1.5 rounded-md border border-[#E5E0D8] bg-white text-xs focus:outline-none focus:ring-1 focus:ring-[#5B7B53]/30"
          data-testid={`${testid}-url`}
        />
      </div>
    </div>
  );
}
